/**
 * FOCUS (B10) — where focus goes when the thing holding it leaves (§H.1).
 *
 *  - Card closes (Esc, Close details, Back) while focus is in the card, or focus
 *    fell to <body>: focus returns to what opened it (the listbox, a row or a pin).
 *    Closing by clicking the map leaves focus where the user put it.
 *  - The opener has gone (a pin left the pool) or is hidden: focus moves to the map region.
 *  - Drive starts and focus is stranded in the inert chrome: "End the drive"
 *    ([data-ld-drive-focus]) takes it. Drive ends and focus is lost: the card
 *    heading (#ld-card-title, tabindex -1) takes it once the card is back.
 *
 * Markers the layout relies on (documented for B2, B7, B8):
 *   [data-ld-card-slot] / #ld-card   the card's container (the layout marks its slots)
 *   #ld-card-title                   the card heading (tabindex -1)
 *   [aria-roledescription="map"]     the map region (B2)
 *   [data-ld-drive-focus]            the "End the drive" button (B8)
 */
import { useEffect } from "react";
import { appStore } from "@/store/store";

export const CARD_SELECTOR = "[data-ld-card-slot], #ld-card";
export const MAP_REGION_SELECTOR = '[aria-roledescription="map"]';
/** Where focus may stay during a drive. */
const DRIVE_SAFE_SELECTOR = '[data-ld-slot="overlays"], [data-ld-drive="live"], [data-ld-slot="map"]';
/** The drive HUD (B8), including its "End the drive" button. */
const DRIVE_HUD_SELECTOR = "[data-ld-drive-focus], .ld-loc-card-hud";

export function activeElement(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const a = document.activeElement;
  return a instanceof HTMLElement && a !== document.body ? a : null;
}

const inCard = (el: Element | null): boolean => !!el?.closest(CARD_SELECTOR);

/** Focus is nowhere useful: on <body>, gone, or inside an inert subtree. */
export function focusLost(el: HTMLElement | null = activeElement()): boolean {
  return !el || !el.isConnected || !!el.closest("[inert]");
}

function focusable(el: HTMLElement | null): el is HTMLElement {
  if (!el || !el.isConnected || el.closest("[inert]")) return false;
  const vis = (el as HTMLElement & { checkVisibility?: (o?: object) => boolean }).checkVisibility;
  return typeof vis === "function" ? vis.call(el, { visibilityProperty: true }) : true;
}

/** Focuses without scrolling. true when it took. */
export function focusSafely(el: HTMLElement | null): boolean {
  if (!focusable(el)) return false;
  el.focus({ preventScroll: true });
  return document.activeElement === el;
}

/** The map region, or its canvas, or the layout's map slot. */
export function focusMapRegion(): boolean {
  if (typeof document === "undefined") return false;
  const region = document.querySelector<HTMLElement>(MAP_REGION_SELECTOR);
  if (region) {
    if (region.tabIndex < 0 && !region.hasAttribute("tabindex")) region.tabIndex = -1;
    if (focusSafely(region)) return true;
    if (focusSafely(region.querySelector<HTMLElement>("canvas"))) return true;
  }
  return focusSafely(document.querySelector<HTMLElement>('[data-ld-slot="map"]'));
}

/** Retries `fn` each 60 ms until it returns true or `ms` passes (a card arriving, a button mounting). */
function retry(fn: () => boolean, ms: number): () => void {
  let stopped = false;
  const start = performance.now();
  let t = 0;
  const step = () => {
    if (stopped || fn() || performance.now() - start > ms) return;
    t = window.setTimeout(step, 60);
  };
  t = window.setTimeout(step, 0);
  return () => {
    stopped = true;
    window.clearTimeout(t);
  };
}

/** Focus management for the card and the drive. Mount once (AppLayout does). */
export function useFocusReturn(): void {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let opener: HTMLElement | null = null;
    let cancel: (() => void) | null = null;
    const later = (fn: () => boolean, ms: number) => {
      cancel?.();
      cancel = retry(fn, ms);
    };

    const unsub = appStore.subscribe((s, p) => {
      // Selection opened or moved: remember where it came from (not the card's own prev/next).
      if (s.selectedId && s.selectedId !== p.selectedId) {
        const a = activeElement();
        if (a && !inCard(a)) opener = a;
        return;
      }
      // Selection closed.
      if (!s.selectedId && p.selectedId) {
        const back = opener;
        opener = null;
        const a = activeElement();
        if (a && !inCard(a) && !focusLost(a)) return; // the user is elsewhere (the map, the panel)
        later(() => {
          const now = activeElement();
          if (now && !inCard(now) && !focusLost(now)) return true;
          return focusSafely(back) || focusMapRegion();
        }, 500);
        return;
      }
      // Drive started: the chrome goes inert (§E.6), so focus left anywhere but the
      // overlays, the live zoom island or the map moves to "End the drive".
      if (s.phase === "drive" && p.phase !== "drive") {
        later(() => {
          const a = activeElement();
          if (a && !focusLost(a) && a.closest(DRIVE_SAFE_SELECTOR)) return true;
          return focusSafely(document.querySelector<HTMLElement>("[data-ld-drive-focus]"));
        }, 1200);
        return;
      }
      // Drive ended: the card heading, once the card is back.
      if (p.phase === "drive" && s.phase !== "drive") {
        later(() => {
          // (I1) "End the drive" keeps focus while its HUD plays the leave, then unmounts and drops it to
          // the body: focus still in the drive HUD counts as lost.
          const a = activeElement();
          if (!focusLost(a) && !a?.closest(DRIVE_HUD_SELECTOR)) return true;
          const heading = document.getElementById("ld-card-title");
          if (heading && !heading.hasAttribute("tabindex")) heading.tabIndex = -1;
          return focusSafely(heading);
        }, 1600);
      }
    });
    return () => {
      unsub();
      cancel?.();
    };
  }, []);
}
